import { TYPING_HEARTBEAT_MS, type TypingEvent } from '@veriverse/shared';

/** No keystroke for this long and the "..." goes away, even with text left in the box. */
export const TYPING_IDLE_MS = 5_000;

export interface TypingNotifier {
  /** The chat box changed: `active` is true while there is nearby-chat text in it. */
  input(active: boolean): void;
  /** The message was sent, the box emptied or lost focus: stop at once. */
  stop(): void;
}

/**
 * Tells the server when the user is typing a nearby-chat message (spec §18). Sends `true` on the
 * first keystroke and again every `TYPING_HEARTBEAT_MS` while keys keep coming, and `false` when
 * typing ends or pauses for `TYPING_IDLE_MS`. The server decides who is close enough to see it.
 */
export function createTypingNotifier(
  send: (typing: boolean) => void,
  now: () => number = Date.now,
): TypingNotifier {
  let typing = false;
  let lastSent = 0;
  let idle: ReturnType<typeof setTimeout> | undefined;

  const stop = () => {
    clearTimeout(idle);
    idle = undefined;
    if (!typing) return;
    typing = false;
    send(false);
  };

  return {
    input(active) {
      if (!active) {
        stop();
        return;
      }
      const time = now();
      if (!typing || time - lastSent >= TYPING_HEARTBEAT_MS) {
        typing = true;
        lastSent = time;
        send(true);
      }
      clearTimeout(idle);
      idle = setTimeout(stop, TYPING_IDLE_MS);
    },
    stop,
  };
}

type TypingListener = (event: TypingEvent) => void;
const listeners = new Set<TypingListener>();

/** Typing updates from the server, for the world scene's bubbles. */
export const typingEvents = {
  emit(event: TypingEvent): void {
    for (const listener of listeners) listener(event);
  },
  /** Returns the unsubscribe function. */
  on(listener: TypingListener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};
